import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { awards as awardsService } from "../../api/awards.service.js";
import type { AwardState } from "../../api/awards.types.js";
import EmptyState from "../../components/EmptyState/index.js";
import type { SelectOption } from "../../components/Form/SearchableSelect/index.js";
import LoadingState from "../../components/LoadingState/index.js";
import { ConfirmationModal } from "../../components/Modal/ConfirmationModal.js";
import { useAward, useAwardDictionaries } from "../../hooks/useAwards.js";
import { useRegionNodes } from "../../hooks/useRegions.js";
import { ROUTES } from "../../routes.js";
import { AwardForm } from "./AwardForm.js";
import {
  awardToFormValues,
  emptyAwardFormValues,
  toAwardWriteBody,
  type AwardFormValues,
} from "./awardForm.helpers.js";
import { jurisdictionLabel } from "./components/JurisdictionChips.js";
import styles from "./AwardForm.module.css";

/**
 * Loads what the edit form needs and does nothing else while it is missing.
 * Kept apart from the create path so the create page never waits on a row
 * fetch it does not make.
 */
function EditAwardPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useAward(id);

  const initialValues = useMemo(() => (data ? awardToFormValues(data.award) : null), [data]);

  if (loading) {
    return <LoadingState message="Loading award..." />;
  }

  if (error || !initialValues || !data) {
    const status = error ? (error as Error & { status?: number }).status : undefined;
    const isNotFound = status === 404;
    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title={isNotFound ? "Award not found" : "Failed to load award"}
          message={
            isNotFound
              ? "The award you are looking for does not exist or has been deleted."
              : error?.message
          }
          action={
            isNotFound
              ? { label: "Back to Awards", onClick: () => navigate(ROUTES.awards.list) }
              : { label: "Retry", onClick: () => refetch() }
          }
        />
      </div>
    );
  }

  return (
    <AwardFormShell
      mode="edit"
      awardId={id}
      initialValues={initialValues}
      publishState={data.award.state}
      lastSavedAt={data.award.updatedAt}
    />
  );
}

type AwardFormShellProps = {
  mode: "create" | "edit";
  awardId: string | null;
  initialValues: AwardFormValues;
  publishState: AwardState | null;
  lastSavedAt: string | null;
};

/**
 * The part both modes share: the dictionaries and the country tree, the
 * submit and delete calls, and the navigation back to the list on success.
 *
 * No toast on success. The list has no toast surface and staff-ui's other pages
 * land back on their list with the new row in it; that row, or its absence
 * after a delete, is the confirmation.
 */
function AwardFormShell({
  mode,
  awardId,
  initialValues,
  publishState,
  lastSavedAt,
}: AwardFormShellProps) {
  const navigate = useNavigate();
  const dictionaries = useAwardDictionaries();
  const { nodes } = useRegionNodes();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Country nodes only: the tree mixes regions in, and the two id spaces
  // collide (see useCountryNames). Names are nullable upstream, so the same
  // `Country ${id}` fallback the list chips use stands in for a missing one.
  const countryOptions = useMemo<SelectOption[]>(
    () =>
      nodes
        .filter(node => node.kind === "country")
        .map(node => ({ value: node.id, label: node.name ?? jurisdictionLabel(node.id) })),
    [nodes],
  );

  if (dictionaries.loading) {
    return <LoadingState message="Loading award dictionaries..." />;
  }

  if (dictionaries.error || !dictionaries.data) {
    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Failed to load award dictionaries"
          message={dictionaries.error?.message}
          action={{ label: "Retry", onClick: () => dictionaries.refetch() }}
        />
      </div>
    );
  }

  const handleSubmit = async (values: AwardFormValues, state: AwardState) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = toAwardWriteBody(values, state);
      if (awardId) {
        await awardsService.update(awardId, body);
      } else {
        await awardsService.create(body);
      }
      navigate(ROUTES.awards.list);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save the award");
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!awardId) return;
    await awardsService.remove(awardId);
    navigate(ROUTES.awards.list);
  };

  return (
    <>
      <AwardForm
        mode={mode}
        initialValues={initialValues}
        dictionaries={dictionaries.data}
        countryOptions={countryOptions}
        publishState={publishState}
        lastSavedAt={lastSavedAt}
        submitting={submitting}
        submitError={submitError}
        onSubmit={handleSubmit}
        onCancel={() => navigate(ROUTES.awards.list)}
        onDelete={awardId ? () => setConfirmingDelete(true) : undefined}
      />
      {confirmingDelete && (
        <ConfirmationModal
          title="Delete Award"
          message="Are you sure you want to delete this award?"
          warningMessage="This action cannot be undone. The award and its jurisdictions will be permanently removed."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setConfirmingDelete(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}

/**
 * `/awards/new` and `/awards/:id/edit`, one route component: the presence of
 * `:id` is the whole difference between the two.
 */
export default function AwardFormPage() {
  const { id } = useParams<{ id: string }>();

  if (id) {
    return <EditAwardPage id={id} />;
  }

  const initialValues = emptyAwardFormValues();
  return (
    <AwardFormShell
      mode="create"
      awardId={null}
      initialValues={initialValues}
      publishState={null}
      lastSavedAt={null}
    />
  );
}
